import { queryOptions } from "@tanstack/react-query";
import { RolloutSignalSchema, type RolloutSignal } from "@nexus/contracts";
import { config } from "@/config";
import {
  HEALTH_POLL_INTERVAL,
  HEALTH_STALE_TIME_MS,
  RETRY_COUNT_DEFAULT,
} from "@/lib/query-constants";

/**
 * Whether the web tier has finished rolling out.
 *
 * `/version.json` is answered by whichever pod the load balancer picked, so
 * mid-rollout it can advertise a build that a reload then fails to land on. This
 * asks the API for the tier-wide answer, which Kubernetes tracks, so the banner
 * only offers a reload that will actually deliver the new bundle (AD-2221).
 */
async function fetchRolloutSignal(): Promise<RolloutSignal> {
  const res = await fetch(`${config.apiUrl}/version/rollout`);
  if (!res.ok) throw new Error(`version/rollout returned ${res.status}`);
  const json: unknown = await res.json();
  const parsed = RolloutSignalSchema.safeParse(json);
  if (!parsed.success) throw new Error("invalid version/rollout response");
  return parsed.data;
}

export const rolloutSignalKeys = {
  all: ["rollout-signal"] as const,
} as const;

/** Polls on the health cadence, so convergence is noticed in the same window. */
export function rolloutSignalQueryOptions() {
  return queryOptions({
    queryKey: rolloutSignalKeys.all,
    queryFn: fetchRolloutSignal,
    refetchInterval: HEALTH_POLL_INTERVAL,
    // Matches the health poll: React Query skips interval fetches while the tab
    // is blurred, so without this a backgrounded tab never learns of a rollout.
    refetchOnWindowFocus: true,
    staleTime: HEALTH_STALE_TIME_MS,
    retry: RETRY_COUNT_DEFAULT,
    meta: { skipGlobalError: true },
  });
}
